import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { Search, Plus, Trash2, X } from 'lucide-react';

interface KeyHider {
  id: string;
  truck_unit_number: string;
  person: string;
  status: string;
  date: string | null;
}

export function KeyHiderView() {
  const toast = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [keyHiders, setKeyHiders] = useState<KeyHider[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedKeyHiders, setSelectedKeyHiders] = useState<Set<string>>(new Set());
  const [editingCell, setEditingCell] = useState<{ id: string; field: 'person' | 'status' } | null>(null);
  const [editValue, setEditValue] = useState('');
  
  const [formData, setFormData] = useState({
    truck_unit_number: '',
    person: '',
    status: 'Ima',
    date: ''
  });

  useEffect(() => {
    loadKeyHiders();
  }, []);

  async function loadKeyHiders() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('key_hiders')
        .select('*')
        .order('truck_unit_number');

      if (error) throw error;
      setKeyHiders(data || []);
    } catch (error) {
      console.error('Error loading key hiders:', error);
      toast.error('Failed to load key hiders');
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('key_hiders')
        .insert([{
          truck_unit_number: formData.truck_unit_number,
          person: formData.person,
          status: formData.status,
          date: formData.date || null
        }]);

      if (error) throw error;

      toast.success('Key hider added successfully!');
      setShowModal(false);
      setFormData({ truck_unit_number: '', person: '', status: 'Ima', date: '' });
      loadKeyHiders();
    } catch (error) {
      console.error('Error adding key hider:', error);
      toast.error('Failed to add key hider');
    } finally {
      setLoading(false);
    }
  }

  async function handleBulkDelete() {
    try {
      setLoading(true);
      const selectedIds = Array.from(selectedKeyHiders);
      
      const { error } = await supabase
        .from('key_hiders')
        .delete()
        .in('id', selectedIds);

      if (error) throw error;

      toast.success(`Deleted ${selectedIds.length} key hider(s)`);
      setSelectedKeyHiders(new Set());
      loadKeyHiders();
    } catch (error) {
      console.error('Error deleting key hiders:', error);
      toast.error('Failed to delete key hiders');
    } finally {
      setLoading(false);
    }
  }

  function startEditing(id: string, field: 'person' | 'status', currentValue: string) {
    setEditingCell({ id, field });
    setEditValue(currentValue);
  }

  async function saveEdit(id: string, field: 'person' | 'status') {
    try {
      const { error } = await supabase
        .from('key_hiders')
        .update({ [field]: editValue })
        .eq('id', id);

      if (error) throw error;

      toast.success(`Updated ${field} successfully!`);
      setEditingCell(null);
      setEditValue('');
      loadKeyHiders();
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      toast.error(`Failed to update ${field}`);
    }
  }

  async function toggleStatus(id: string, currentStatus: string) {
    try {
      const newStatus = currentStatus === 'Ima' ? 'Nema' : 'Ima';
      
      // Optimistic update
      setKeyHiders(prev => prev.map(kh => 
        kh.id === id ? { ...kh, status: newStatus } : kh
      ));
      
      const { error } = await supabase
        .from('key_hiders')
        .update({ status: newStatus })
        .eq('id', id);

      if (error) {
        // Revert on error
        setKeyHiders(prev => prev.map(kh => 
          kh.id === id ? { ...kh, status: currentStatus } : kh
        ));
        throw error;
      }

      toast.success(`Status changed to ${newStatus}`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  }

  function toggleSelectKeyHider(id: string) {
    setSelectedKeyHiders(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function toggleSelectAll(ids: string[]) {
    const allSelected = ids.every(id => selectedKeyHiders.has(id));
    if (allSelected) {
      setSelectedKeyHiders(prev => {
        const next = new Set(prev);
        ids.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setSelectedKeyHiders(prev => {
        const next = new Set(prev);
        ids.forEach(id => next.add(id));
        return next;
      });
    }
  }

  const filteredKeyHiders = keyHiders.filter(kh => {
    const searchLower = searchTerm.toLowerCase();
    return !searchTerm || 
      kh.truck_unit_number.toLowerCase().includes(searchLower) ||
      kh.person.toLowerCase().includes(searchLower) ||
      kh.status.toLowerCase().includes(searchLower);
  });

  return (
    <div className="space-y-6">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-slate-900">Key Hider on Trucks</h1>
        <p className="text-slate-600 mt-1">Track key holder information for trucks</p>
      </div>
      
      <div className="flex items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search trucks, person, or status..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <button 
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
        >
          <Plus className="w-5 h-5" />
          Add Key Hider
        </button>
      </div>

      {/* Key Hiders Table - Monday.com Style */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-3 py-2 text-center w-16">
                  <input
                    type="checkbox"
                    checked={filteredKeyHiders.length > 0 && filteredKeyHiders.every(kh => selectedKeyHiders.has(kh.id))}
                    onChange={() => toggleSelectAll(filteredKeyHiders.map(kh => kh.id))}
                    className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                  />
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase w-1/4">Truck Unit Number</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase w-1/4">Person</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase w-1/4">Status</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase w-1/4">Date</th>
              </tr>
            </thead>
            <tbody>
              {filteredKeyHiders.map((keyHider) => (
                      <tr key={keyHider.id} className="border-b border-slate-200 hover:bg-slate-50/50 transition-all">
                        <td className="px-3 py-2 text-center w-16">
                          <input
                            type="checkbox"
                            checked={selectedKeyHiders.has(keyHider.id)}
                            onChange={() => toggleSelectKeyHider(keyHider.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-2 w-1/4">
                          <span className="text-sm font-bold text-blue-600">{keyHider.truck_unit_number}</span>
                        </td>
                        <td className="px-4 py-2 w-1/4">
                          {editingCell?.id === keyHider.id && editingCell?.field === 'person' ? (
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={() => saveEdit(keyHider.id, 'person')}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveEdit(keyHider.id, 'person');
                                if (e.key === 'Escape') setEditingCell(null);
                              }}
                              autoFocus
                              className="w-full px-2 py-1 text-sm font-semibold text-slate-900 border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          ) : (
                            <span 
                              className="text-sm font-semibold text-slate-900 cursor-pointer hover:bg-blue-50 px-2 py-1 rounded transition-colors"
                              onClick={() => startEditing(keyHider.id, 'person', keyHider.person)}
                            >
                              {keyHider.person}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 w-1/4">
                          <button
                            key={`${keyHider.id}-${keyHider.status}`}
                            onClick={() => toggleStatus(keyHider.id, keyHider.status)}
                            className={`px-3 py-1 rounded-lg text-sm font-semibold transition-colors ${
                              keyHider.status === 'Ima' 
                                ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                                : 'bg-red-100 text-red-700 hover:bg-red-200'
                            }`}
                          >
                            {keyHider.status}
                          </button>
                        </td>
                        <td className="px-4 py-2 w-1/4">
                          <span className="text-sm text-slate-700">
                            {keyHider.date ? new Date(keyHider.date).toLocaleDateString() : '-'}
                          </span>
                        </td>
                      </tr>
                    ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Key Hider Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
              <h3 className="text-xl font-bold text-slate-900">Add Key Hider</h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Truck Unit Number <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={formData.truck_unit_number}
                  onChange={(e) => setFormData({ ...formData, truck_unit_number: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Person <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={formData.person}
                  onChange={(e) => setFormData({ ...formData, person: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Status</label>
                <input
                  type="text"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Date</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-6 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50"
                >
                  {loading ? 'Adding...' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Floating Action Bar */}
      {selectedKeyHiders.size > 0 && (
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 px-6 py-4 flex items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                {selectedKeyHiders.size}
              </div>
              <span className="font-semibold text-slate-900">
                {selectedKeyHiders.size === 1 ? 'Item' : 'Items'} selected
              </span>
            </div>

            <div className="h-8 w-px bg-slate-300"></div>

            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <Trash2 className="w-5 h-5" />
              <span className="font-medium">Delete</span>
            </button>

            <button
              onClick={() => setSelectedKeyHiders(new Set())}
              className="ml-2 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
